import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import {
  convertLead,
  getConvertPreview,
  getLeadDuplicateCandidates,
  LeadConvertPreview,
  LeadDuplicateCandidate,
} from "../../api/leadsAPI";
import { joinName, formatPersonName } from "../../utils/personName";

/**
 * The Convert to Member dialog: the ONE implementation.
 *
 * It lived inside the Lead Pipeline drawer. It is its own component so the
 * staff billing screens can run the very same dialog in place when staff
 * choose "Convert to member, then continue" on a lead's account
 * (hooks/useLeadBillingPrompt), without leaving the page and losing what they
 * typed. The drawer renders this too, so there is still one dialog, one
 * preview and one convert call.
 *
 * It reads what converting will bill from the server (the same decision the
 * conversion itself uses), looks for an existing family to merge into, lets
 * staff convert without billing, and handles the "registration wizard failed
 * earlier" acknowledgment.
 */

const formatMoney = (amount: number) => `$${(Number(amount) || 0).toFixed(2)}`;

/**
 * The card payment the family made at online sign-up, as one line for staff.
 * 2026-10-06: a family charged $94 at sign-up was billed the $94 again when
 * converted, and nothing on this dialog said they had paid.
 */
export function signupPaymentLine(preview: Pick<LeadConvertPreview, "signup_payment">): string | null {
  const paid = preview.signup_payment;
  if (!paid) return null;
  const amount = paid.amount != null ? `${formatMoney(paid.amount)} ` : "";
  if (paid.already_recorded) {
    return `Leave the ${amount}card payment taken at sign-up as it is: it is already on their account.`;
  }
  return `Apply the ${amount}card payment taken at sign-up (Stripe ${paid.payment_intent_id}) to their account, after checking it with Stripe, so it is not billed again.`;
}

/**
 * What converting will do, as plain lines for the Convert dialog. Built from
 * what the server says it will bill (the same decision the conversion itself
 * uses), never from a guess in the browser.
 */
export function convertBillingLines(preview: LeadConvertPreview, billNow: boolean): string[] {
  const lines: string[] = [];
  const fee = preview.registration_fee;
  const classes = preview.classes_total;

  const signupLine = signupPaymentLine(preview);
  if (signupLine) lines.push(signupLine);

  if (!billNow) {
    lines.push("Not create any invoice. No registration fee and no class charges are billed now.");
    if (classes > 0) {
      lines.push(
        `Not enroll the ${classes === 1 ? "class" : `${classes} classes`} the lead picked. Enroll from their membership page when you are ready; that is when billing starts.`
      );
    }
    if (fee.configured_amount > 0 && fee.reason !== "fee_already_exists") {
      lines.push(
        `The ${formatMoney(fee.configured_amount)} registration fee is billed later, when you enroll an athlete.`
      );
    }
  } else {
    if (fee.will_bill) {
      lines.push(`Bill the ${formatMoney(fee.amount)} registration fee: a new invoice on their account.`);
    } else if (fee.reason === "fee_already_exists") {
      lines.push("Not bill a registration fee: they already have one on their account.");
    } else {
      lines.push("Not bill a registration fee: your club has none set.");
    }
    if (classes > 0) {
      lines.push(
        `Enroll the ${classes === 1 ? "class" : `${classes} classes`} the lead picked and create a first invoice for ${classes === 1 ? "it" : "them"}. Monthly dues follow from the next billing run.`
      );
    } else {
      lines.push("Not start monthly dues: no classes are enrolled. Dues begin when an athlete is enrolled in a class.");
    }
  }

  lines.push("Create a sign-in for the family. No email is sent.");
  if (preview.existing_invoice_count > 0) {
    lines.push(
      `Leave the ${preview.existing_invoice_count === 1 ? "invoice" : `${preview.existing_invoice_count} invoices`} already on their account as ${preview.existing_invoice_count === 1 ? "it is" : "they are"}.`
    );
  }
  return lines;
}

type ConvertResponse = Awaited<ReturnType<typeof convertLead>>;

export type ConvertLeadOutcome =
  | { merged: true; mergeTargetId: string; response: ConvertResponse }
  | { merged: false; response: ConvertResponse };

interface ConvertAthlete {
  first_name?: string | null;
  last_name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  programs?: { slot?: unknown }[] | null;
}

export interface ConvertLeadDialogProps {
  token: string;
  /** The lead's id (a lead is a membership row, so this is the membership id too). */
  leadId: string;
  /** Shown in "This will create an active membership for ...". */
  leadName: string;
  /** Athletes the lead listed, when the caller has them. */
  athletes?: ConvertAthlete[] | null;
  /**
   * Offer "merge into an existing member". Off where the caller needs the
   * SAME account to still exist afterward (a billing screen mid-entry): a
   * merge removes this record.
   */
  allowMerge?: boolean;
  onClose: () => void;
  onConverted: (outcome: ConvertLeadOutcome) => void;
}

const ConvertLeadDialog: React.FC<ConvertLeadDialogProps> = ({
  token,
  leadId,
  leadName,
  athletes,
  allowMerge = true,
  onClose,
  onConverted,
}) => {
  const { confirm, confirmModal } = useConfirmModal();
  // Existing memberships that look like the same person (same phone/email/
  // name). Staff can convert into one of them — a real merge that re-keys the
  // lead's records and removes the lead — instead of creating a duplicate.
  const [duplicateCandidates, setDuplicateCandidates] = useState<LeadDuplicateCandidate[]>([]);
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  // What converting will bill, read when the dialog opens.
  const [convertPreview, setConvertPreview] = useState<LeadConvertPreview | null>(null);
  const [convertPreviewState, setConvertPreviewState] = useState<"idle" | "loading" | "ready" | "error">("loading");
  const [convertBillNow, setConvertBillNow] = useState(true);
  const [converting, setConverting] = useState(false);

  // Ask the server what converting will bill and create, so the dialog can
  // say it before staff confirm (Chris, 2026-10-07: converting "automatically
  // generates an invoice" nobody had been told about).
  useEffect(() => {
    let cancelled = false;
    setConvertBillNow(true);
    setConvertPreview(null);
    setConvertPreviewState("loading");
    getConvertPreview(token, leadId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) {
          setConvertPreview(res.data);
          setConvertPreviewState("ready");
        } else {
          setConvertPreviewState("error");
        }
      })
      .catch(() => {
        // The dialog says it could not check; converting is still possible.
        if (!cancelled) setConvertPreviewState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, token]);

  // Look for existing memberships that match this lead (phone/email/name) so
  // staff can merge instead of duplicating.
  useEffect(() => {
    setMergeTargetId(null);
    let cancelled = false;
    getLeadDuplicateCandidates(token, leadId)
      .then((res) => {
        // Only members are merge targets here: merging into another lead
        // leaves a lead, not a member (the backend rejects it).
        if (!cancelled) {
          setDuplicateCandidates((res?.data || []).filter((c) => c.account_type !== "lead"));
        }
      })
      .catch(() => {
        // Candidate lookup is best-effort — a failure must never block convert.
        if (!cancelled) setDuplicateCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, token]);

  const handleConvertToMember = async (acknowledgeWizardFailed = false) => {
    if (converting && !acknowledgeWizardFailed) return;

    try {
      setConverting(true);
      const response = await convertLead(token, leadId, {
        acknowledge_wizard_failed: acknowledgeWizardFailed || undefined,
        membership_id: mergeTargetId || undefined,
        // Only sent when staff chose not to bill; otherwise the request is
        // exactly what it always was.
        ...(!mergeTargetId && !convertBillNow ? { bill_now: false } : {}),
      });

      if (response.success && response.merged && mergeTargetId) {
        const target = duplicateCandidates.find((c) => c.id === mergeTargetId);
        const targetName = target
          ? `${target.first_name || ""} ${target.last_name || ""}`.trim()
          : "the existing member";
        toast.success(
          `Lead merged into ${targetName} — its notes and history moved over and the duplicate lead was removed.`
        );
        onConverted({ merged: true, mergeTargetId, response });
        return;
      }

      // Backend returns 409 with code WIZARD_FAILED_NEEDS_ACKNOWLEDGEMENT
      // when the lead came from an online registration that did not finish.
      // The card is charged BEFORE the registration is saved, so say whether
      // a payment was taken (it used to say "No Stripe charge" every time).
      if (!response.success && response.code === "WIZARD_FAILED_NEEDS_ACKNOWLEDGEMENT") {
        const reason = response.wizard_failed?.reason || "unknown";
        const at = response.wizard_failed?.at
          ? new Date(response.wizard_failed.at).toLocaleString()
          : "unknown time";
        const original = response.wizard_failed?.original_message
          ? `\n\nOriginal error: ${response.wizard_failed.original_message}`
          : "";
        const paid = response.signup_payment;
        const paymentText = paid
          ? `The family's card WAS charged${paid.amount != null ? ` ${formatMoney(paid.amount)}` : ""} at sign-up (Stripe ${paid.payment_intent_id}). ` +
            `Converting checks that payment with Stripe and puts it on their account, so it is not billed again.`
          : `Club Central has no record of a card payment for it. Check the club's Stripe account before billing the family.`;
        const proceed = await confirm({
          title: "Online registration did not finish",
          message:
            `⚠️ The online registration for this lead did not finish (${at}, reason: ${reason}), so no membership or enrollment was created.\n\n` +
            `${paymentText}${original}\n\n` +
            `Convert this lead now?`,
          confirmText: "Convert anyway",
        });
        if (!proceed) return;
        await handleConvertToMember(true);
        return;
      }

      if (response.success) {
        const enrollCount = response.enrollments?.filter((e: { error?: unknown }) => !e.error).length || 0;
        const fee = response.billing?.registration_fee;
        const billedParts = [
          enrollCount > 0 ? `${enrollCount} enrollment(s) and prorated invoice(s)` : "",
          fee?.billed ? `a ${formatMoney(fee.amount)} registration fee invoice` : "",
        ].filter(Boolean);
        const signup = response.billing?.signup_payment;
        const paidText =
          signup?.applied && signup.amount != null
            ? ` The ${formatMoney(signup.amount)} card payment taken at sign-up was applied to their account.`
            : "";
        toast.success(
          (response.billing && !response.billing.billed_now
            ? "Member created. Nothing was billed."
            : `Member created${billedParts.length > 0 ? ` with ${billedParts.join(" and ")}` : ""}`) + paidText
        );
        onConverted({ merged: false, response });
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || "Failed to convert lead");
    } finally {
      setConverting(false);
    }
  };

  const athleteList = Array.isArray(athletes) ? athletes : [];

  return (
    // z-[70]: opens on top of the lead drawer and of any dialog that asked for it.
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="convert-lead-dialog-title"
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <h3 id="convert-lead-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Convert to Member
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-2">
          This will create an active membership for <strong>{leadName}</strong>.
        </p>
        {duplicateCandidates.length > 0 && allowMerge && (
          <div className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
              ⚠️ This lead looks like it may already exist. Merge instead of
              creating a duplicate?
            </p>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-1">
              <input
                type="radio"
                name="convert-target"
                checked={mergeTargetId === null}
                onChange={() => setMergeTargetId(null)}
                className="mt-0.5"
              />
              <span>Create a new member (no match)</span>
            </label>
            {duplicateCandidates.map((c) => (
              <label
                key={c.id}
                className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-1"
              >
                <input
                  type="radio"
                  name="convert-target"
                  checked={mergeTargetId === c.id}
                  onChange={() => setMergeTargetId(c.id)}
                  className="mt-0.5"
                />
                <span>
                  Merge into{" "}
                  <strong>
                    {formatPersonName(c, "(no name)")}
                  </strong>{" "}
                  <span className="opacity-70">
                    ({c.email || "no email"} · {c.account_type || "member"}/{c.status || "?"}
                    {c.match_reasons?.length
                      ? ` · matched on ${c.match_reasons.join(", ")}`
                      : ""})
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        {duplicateCandidates.length > 0 && !allowMerge && (
          <div
            data-testid="convert-possible-duplicate"
            className="mb-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200"
          >
            <p className="font-medium mb-1">This lead looks like a family that already exists:</p>
            <ul className="list-disc pl-5">
              {duplicateCandidates.map((c) => (
                <li key={c.id}>
                  {formatPersonName(c, "(no name)")} ({c.email || "no email"})
                </li>
              ))}
            </ul>
            <p className="mt-1">
              Converting here makes a second record. To merge them instead, cancel and use
              Convert to Member on the Lead Pipeline.
            </p>
          </div>
        )}
        {mergeTargetId && (
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
            The lead&apos;s notes, athletes, and history will move to the selected
            member, and this duplicate lead record will be removed. No new
            membership, enrollments, or invoices are created.
          </p>
        )}
        {mergeTargetId && convertPreview && signupPaymentLine(convertPreview) && (
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4" data-testid="convert-signup-payment">
            {signupPaymentLine(convertPreview)}
          </p>
        )}
        {!mergeTargetId && athleteList.length > 0 && (
          <div className="text-sm text-gray-500 dark:text-gray-500 mb-2">
            <p className="font-medium mb-1">{athleteList.length} athlete(s) will be created:</p>
            <ul className="list-disc list-inside">
              {athleteList.map((a, i) => (
                <li key={i}>
                  {joinName(a.first_name || a.firstName, a.last_name || a.lastName) || `Athlete ${i + 1}`}
                  {Array.isArray(a.programs) && a.programs.filter((p) => p.slot).length > 0
                    ? ` — ${a.programs.filter((p) => p.slot).length} class(es)`
                    : " — no classes selected"}
                </li>
              ))}
            </ul>
          </div>
        )}
        {!mergeTargetId && (
          <div
            className="text-sm text-gray-700 dark:text-gray-300 mb-4"
            data-testid="convert-what-happens"
          >
            <p className="mb-2 text-gray-500 dark:text-gray-400">
              You do not need to convert a lead to send them a bill or a message. Find them
              in the search bar and use their membership page. Converting makes them an
              active member and marks the lead as won.
            </p>
            {convertPreviewState === "loading" && (
              <p className="text-gray-500 dark:text-gray-400">Checking what will be billed…</p>
            )}
            {convertPreviewState === "error" && (
              <p role="alert" className="text-amber-700 dark:text-amber-300">
                Could not check what will be billed. Converting may add a registration fee
                invoice and invoices for any classes the lead picked. Choose "Do not bill
                anything yet" below if you do not want that.
              </p>
            )}
            {convertPreviewState === "ready" && convertPreview && (
              <>
                <p className="font-medium text-gray-900 dark:text-white mb-1">
                  {convertBillNow ? "Converting now will:" : "Converting without billing will:"}
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  {convertBillingLines(convertPreview, convertBillNow).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </>
            )}
            {(convertPreviewState === "error" ||
              (convertPreview &&
                (convertPreview.registration_fee.will_bill || convertPreview.will_enroll))) && (
              <fieldset className="mt-3 space-y-1.5">
                <legend className="sr-only">Billing when converting</legend>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="convert-billing"
                    checked={convertBillNow}
                    onChange={() => setConvertBillNow(true)}
                    className="mt-0.5 accent-[var(--cc-action)]"
                  />
                  <span>Bill now</span>
                </label>
                <label className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="convert-billing"
                    checked={!convertBillNow}
                    onChange={() => setConvertBillNow(false)}
                    className="mt-0.5 accent-[var(--cc-action)]"
                  />
                  <span>
                    Do not bill anything yet
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      No invoice is created and no classes are enrolled. Billing starts when
                      you enroll an athlete from their membership page.
                    </span>
                  </span>
                </label>
              </fieldset>
            )}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleConvertToMember()}
            disabled={converting}
            className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-60 ${
              mergeTargetId
                ? "bg-amber-600 hover:bg-amber-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {converting
              ? "Converting…"
              : mergeTargetId
                ? "Merge into Existing"
                : convertBillNow
                  ? "Convert"
                  : "Convert without billing"}
          </button>
        </div>
      </div>
      {confirmModal}
    </div>
  );
};

export default ConvertLeadDialog;
